import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type { MerchantSettlementConfigDto } from "@/features/merchant/api/dto";

/**
 * Met à jour la configuration de reversement
 * (`PUT /MerchantSettlementConfigs/merchant/{merchantId}?actingMerchantId=...`).
 * Rôle : merchant avec permission `MANAGE_SETTLEMENT_CONFIG` ou back-office.
 */
export const useUpdateSettlementConfig = (merchantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "settlement-config", "update", merchantId],
    mutationFn: ({
      body,
      actingMerchantId,
    }: {
      body: MerchantSettlementConfigDto;
      actingMerchantId?: string;
    }) => {
      const query = actingMerchantId
        ? `?actingMerchantId=${actingMerchantId}`
        : "";
      return mutateData({
        url: `${merchantEndpoints.merchantSettlementConfigs.update(merchantId)}${query}`,
        reqBody: body as unknown as Record<string, unknown>,
        method: "PUT",
      }) as Promise<MerchantSettlementConfigDto>;
    },
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        merchantWriteCacheKeys(merchantId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateSettlementConfig failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
